import type { IEntity, IPoint } from "dxf-parser";
import type DxfArrayScanner from "dxf-parser/dist/DxfArrayScanner";
import type { IGroup } from "dxf-parser/dist/DxfArrayScanner";

/**
 * @module - THE FUNCTIONS IN THIS FILE ARE DIRECTLY COPIED FROM THE DXF-PARSER LIBRARY AND SHOULD
 * BE MODIFIED WITH EXTREME CAUTION
 *
 * @see {@link https://github.com/gdsestimating/dxf-parser/blob/master/src/ParseHelpers.ts}
 * @see {@link https://github.com/gdsestimating/dxf-parser/blob/master/src/AutoCadColorIndex.ts}
 */

/**
 * Returns the truecolor value of the given AutoCad color index value
 * @return {Number} truecolor value as a number
 */
export function getAcadColor(index: number) {
  return AUTO_CAD_COLOR_INDEX[index];
}

/**
 * Parses the 2D or 3D coordinate, vector, or point. When complete,
 * the scanner remains on the last group of the coordinate.
 * @param {*} scanner
 */
export function parsePoint(scanner: DxfArrayScanner) {
  const point = {} as IPoint;

  // Reread group for the first coordinate
  scanner.rewind();
  let curr = scanner.next();

  let code = curr.code;
  point.x = curr.value as number;

  code += 10;
  curr = scanner.next();
  if (curr.code !== code) {
    throw new Error("Expected code for point value to be " + code + " but got " + curr.code + ".");
  }
  point.y = curr.value as number;

  code += 10;
  curr = scanner.next();
  if (curr.code !== code) {
    // Only the x and y are specified. Don't read z.
    scanner.rewind(); // Let the calling code advance off the point
    return point;
  }
  point.z = curr.value as number;

  return point;
}

/**
 * Parses 16 numbers as an array. When complete,
 * the scanner remains on the last group of the value.
 * @param {*} scanner
 * @param {*} groupCode
 */
export function parseMatrix(scanner: DxfArrayScanner, groupCode: number) {
  // Reread group for the first coordinate
  scanner.rewind();
  const matrix: number[] = [];

  for (let i = 0; i < 16; i++) {
    const curr = scanner.next();
    if (curr.code !== groupCode) {
      throw new Error("Expected code for matrix value to be " + groupCode + " but got " + curr.code + ".");
    }

    matrix.push(curr.value as number);
  }
  return matrix;
}

/**
 * Attempts to parse codes common to all entities. Returns true if the group
 * was handled by this function.
 * @param {*} entity - the entity currently being parsed
 * @param {*} curr - the current group being parsed
 */
export function checkCommonEntityProperties(entity: IEntity, curr: IGroup, scanner: DxfArrayScanner) {
  switch (curr.code) {
    case 0:
      entity.type = curr.value as string;
      break;
    case 5:
      entity.handle = curr.value as number;
      break;
    case 6:
      entity.lineType = curr.value as string;
      break;
    case 8: // Layer name
      entity.layer = curr.value as string;
      break;
    case 48:
      entity.lineTypeScale = curr.value as number;
      break;
    case 60:
      entity.visible = curr.value === 0;
      break;
    case 62: // Acad Index Color. 0 inherits ByBlock. 256 inherits ByLayer. Default is bylayer
      entity.colorIndex = curr.value as number;
      entity.color = getAcadColor(Math.abs(curr.value as number));
      break;
    case 67:
      entity.inPaperSpace = curr.value !== 0;
      break;
    case 100:
      //ignore
      break;
    case 101: // Embedded Object in ACAD 2018.
      // See https://ezdxf.readthedocs.io/en/master/dxfinternals/dxftags.html#embedded-objects
      while (curr.code !== 0) {
        curr = scanner.next();
      }
      scanner.rewind();
      break;
    case 330:
      entity.ownerHandle = curr.value as string;
      break;
    case 347:
      entity.materialObjectHandle = curr.value as number;
      break;
    case 370:
      //From https://www.woutware.com/Forum/Topic/955/lineweight?returnUrl=%2FForum%2FUserPosts%3FuserId%3D478262319
      // An integer representing 100th of mm, must be one of the following values:
      // 0, 5, 9, 13, 15, 18, 20, 25, 30, 35, 40, 50, 53, 60, 70, 80, 90, 100, 106, 120, 140, 158, 200, 211.
      // -3 = STANDARD, -2 = BYLAYER, -1 = BYBLOCK
      entity.lineweight = curr.value as
        | 0
        | 5
        | 9
        | 13
        | 15
        | 18
        | 20
        | 25
        | 30
        | 35
        | 40
        | 50
        | 53
        | 60
        | 70
        | 80
        | 90
        | 100
        | 106
        | 120
        | 140
        | 158
        | 200
        | 211
        | -3
        | -2
        | -1;
      break;
    case 420: // TrueColor Color
      entity.color = curr.value as number;
      break;
    case 1000:
      entity.extendedData = entity.extendedData || ({} as any);
      entity.extendedData.customStrings = entity.extendedData.customStrings || [];
      entity.extendedData.customStrings.push(curr.value as string);
      break;
    case 1001:
      entity.extendedData = entity.extendedData || ({} as any);
      entity.extendedData.applicationName = curr.value as string;
      break;
    default:
      return false;
  }
  return true;
}

const AUTO_CAD_COLOR_INDEX = [
  0, 16711680, 16776960, 65280, 65535, 255, 16711935, 16777215, 8421504, 12632256, 16711680, 16744319, 13369344,
  13395558, 10027008, 10046540, 8323072, 8339263, 4980736, 4990502, 16727808, 16752511, 13382400, 13401958, 10036736,
  10051404, 8331008, 8343359, 4985600, 4992806, 16744192, 16760703, 13395456, 13408614, 10046464, 10056268, 8339200,
  8347455, 4990464, 4995366, 16760576, 16768895, 13408512, 13415014, 10056192, 10061132, 8347392, 8351551, 4995328,
  4997670, 16776960, 16777087, 13421568, 13421670, 10000384, 10000460, 8355584, 8355647, 5000192, 5000230, 12582656,
  14679935, 10079232, 11717734, 7510016, 8755276, 6258432, 7307071, 3755008, 4344870, 8388352, 12582783, 6736896,
  10079334, 5019648, 7510092, 4161280, 6258495, 2509824, 3755046, 4194048, 10485631, 3394560, 8375398, 2529280, 6264908,
  2064128, 5209919, 1264640, 3099686, 65280, 8388479, 52224, 6736998, 38912, 5019724, 32512, 4161343, 19456, 2509862,
  65343, 8388511, 52275, 6737023, 38950, 5019743, 32543, 4161359, 19475, 2509871, 65407, 8388543, 52326, 6737049, 38988,
  5019762, 32575, 4161375, 19494, 2509881, 65471, 8388575, 52377, 6737074, 39026, 5019781, 32607, 4161391, 19513,
  2509890, 65535, 8388607, 52428, 6737100, 39064, 5019800, 32639, 4161407, 19532, 2509900, 49151, 8380415, 39372,
  6730444, 29336, 5014936, 24447, 4157311, 14668, 2507340, 32767, 8372223, 26316, 6724044, 19608, 5010072, 16255,
  4153215, 9804, 2505036, 16383, 8364031, 13260, 6717388, 9880, 5005208, 8063, 4149119, 4940, 2502476, 255, 8355839,
  204, 6710988, 152, 5000344, 127, 4145023, 76, 2500172, 4129023, 10452991, 3342540, 8349388, 2490520, 6245528, 2031743,
  5193599, 1245260, 3089996, 8323327, 12550143, 6684876, 10053324, 4980888, 7490712, 4128895, 6242175, 2490444, 3745356,
  12517631, 14647295, 10027212, 11691724, 7471256, 8735896, 6226047, 7290751, 3735628, 4335180, 16711935, 16744447,
  13369548, 13395660, 9961624, 9981080, 8323199, 8339327, 4980812, 4990540, 16711871, 16744415, 13369497, 13395634,
  9961586, 9981061, 8323167, 8339311, 4980793, 4990530, 16711807, 16744383, 13369446, 13395609, 9961548, 9981042,
  8323135, 8339295, 4980774, 4990521, 16711743, 16744351, 13369395, 13395583, 9961510, 9981023, 8323103, 8339279,
  4980755, 4990511, 3355443, 5987163, 8684676, 11382189, 14079702, 16777215,
];
